/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// LocationEditorModal — create / edit a single retail location. Ported from the
// source app; the form itself is unchanged. Adapted for Brand Command:
// the service takes (id, fields) over RLS instead of ({ callerEmail, id, fields })
// through a service-role edge function, so there is no callerEmail prop.
// All values are re-validated in the service and again by RLS/CHECK constraints;
// this form is convenience, not the gate.
import React, { useState, useEffect, useMemo } from "react";
import { createLocation, updateLocation } from "../services/retailLocationService.js";
import { US_STATES, EMPTY_LOCATION } from "./constants.js";
import {
  MAX_PRODUCTS, matchProductName, normalizeProductKey, buildProductVocabulary,
} from "../utils/productCatalog.js";

export default function LocationEditorModal({ location, vocab, onClose, onSaved }) {
  const isEdit = !!location?.id;
  const [form, setForm] = useState(EMPTY_LOCATION);
  // Backlog item 100: this used to be a comma-separated free-text box, so every
  // typo, casing and pack size a person invented went straight into the public
  // product filter. It is now a picker over the tenant's own flavors — creatable,
  // because a one-off stockist listing must not be blocked by a catalog gap, but
  // an off-catalog entry is flagged rather than accepted silently.
  const [products, setProducts] = useState([]);
  const [productDraft, setProductDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Unconditional hook, then fall back — the modal can open before the tab's
  // catalog load resolves, and an empty vocabulary just means nothing is judged.
  const emptyVocab = useMemo(() => buildProductVocabulary({}), []);
  const catalog = vocab || emptyVocab;
  const listId = "rpf-flavor-options";

  useEffect(() => {
    if (location) {
      setForm({ ...EMPTY_LOCATION, ...location, latitude: location.latitude ?? "", longitude: location.longitude ?? "" });
      setProducts([...(location.products || [])]);
    } else {
      setForm(EMPTY_LOCATION);
      setProducts([]);
    }
    setProductDraft("");
  }, [location]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Catalog names not already chosen — the picker only ever offers what's left.
  const available = useMemo(() => {
    const chosen = new Set(products.map(normalizeProductKey));
    return (catalog.names || []).filter(n => !chosen.has(normalizeProductKey(n)));
  }, [catalog, products]);

  const addProduct = (raw) => {
    const { canonical } = matchProductName(raw, catalog);
    if (!canonical) return;
    setProducts(list => {
      if (list.length >= MAX_PRODUCTS) return list;
      const key = normalizeProductKey(canonical);
      if (list.some(p => normalizeProductKey(p) === key)) return list;
      return [...list, canonical];
    });
    setProductDraft("");
  };

  const removeProduct = (name) => setProducts(list => list.filter(p => p !== name));

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError(null);
    const fields = {
      name:          form.name,
      chain:         form.chain,
      address_line1: form.address_line1,
      address_line2: form.address_line2,
      city:          form.city,
      state:         form.state,
      postal_code:   form.postal_code,
      country:       form.country || "USA",
      latitude:      form.latitude === "" ? null : form.latitude,
      longitude:     form.longitude === "" ? null : form.longitude,
      products,
      notes:         form.notes,
      is_active:     !!form.is_active,
    };
    try {
      const saved = isEdit
        ? await updateLocation(location.id, fields)
        : await createLocation(fields);
      onSaved(saved);
    } catch (e) {
      setError(e.message || "Failed to save location.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rpf-modal-backdrop" onClick={onClose}>
      <div className="rpf-modal" onClick={e => e.stopPropagation()}>
        <div className="rpf-modal-head">
          <h2>{isEdit ? "Edit Retail Location" : "Add Retail Location"}</h2>
          <button className="rpf-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="rpf-modal-body">
          {error && <div className="rpf-alert">{error}</div>}

          {/* Backlog item 126: every placeholder here is a made-up business at a
              made-up address. They used to name a real national retailer, which
              showed through on a sparse row and put that chain's trademark in our
              own marketing capture. Keep them generic. */}
          <div className="rpf-field-grid">
            <label className="rpf-field rpf-col-2">
              <span>Location Name *</span>
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Riverside Market — Downtown" />
            </label>
            <label className="rpf-field">
              <span>Chain / Banner</span>
              <input value={form.chain || ""} onChange={e => set("chain", e.target.value)} placeholder="Regional Grocery Co." />
            </label>

            <label className="rpf-field rpf-col-2">
              <span>Address</span>
              <input value={form.address_line1 || ""} onChange={e => set("address_line1", e.target.value)} placeholder="100 Main St" />
            </label>
            <label className="rpf-field">
              <span>Suite / Unit</span>
              <input value={form.address_line2 || ""} onChange={e => set("address_line2", e.target.value)} />
            </label>

            <label className="rpf-field">
              <span>City</span>
              <input value={form.city || ""} onChange={e => set("city", e.target.value)} />
            </label>
            <label className="rpf-field">
              <span>State</span>
              <select value={form.state || ""} onChange={e => set("state", e.target.value)}>
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="rpf-field">
              <span>ZIP</span>
              <input value={form.postal_code || ""} onChange={e => set("postal_code", e.target.value)} />
            </label>

            <label className="rpf-field">
              <span>Latitude</span>
              <input value={form.latitude} onChange={e => set("latitude", e.target.value)} placeholder="30.4012" inputMode="decimal" />
            </label>
            <label className="rpf-field">
              <span>Longitude</span>
              <input value={form.longitude} onChange={e => set("longitude", e.target.value)} placeholder="-97.7256" inputMode="decimal" />
            </label>
            <div className="rpf-field rpf-col-3">
              <span>
                Products Ordered{" "}
                <em>
                  {catalog.names?.length
                    ? "(pick from your products — these become the filter on your website)"
                    : "(no products set up yet — add them in System Settings → Products)"}
                </em>
              </span>
              <div className="rpf-picker-row">
                <input
                  list={listId}
                  value={productDraft}
                  onChange={e => setProductDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); addProduct(productDraft); }
                  }}
                  placeholder={catalog.names?.length ? `e.g. ${catalog.names[0]}` : "Product name"}
                  disabled={products.length >= MAX_PRODUCTS}
                />
                <datalist id={listId}>
                  {available.map(n => <option key={n} value={n} />)}
                </datalist>
                <button
                  type="button"
                  className="rpf-btn"
                  onClick={() => addProduct(productDraft)}
                  disabled={!productDraft.trim() || products.length >= MAX_PRODUCTS}
                >
                  Add
                </button>
              </div>
              {products.length > 0 && (
                <div className="rpf-chips rpf-chips-edit">
                  {products.map(p => {
                    const off = !matchProductName(p, catalog).matched && !!catalog.names?.length;
                    return (
                      <span
                        key={p}
                        className={`rpf-chip${off ? " rpf-chip-warn" : ""}`}
                        title={off ? "Not in your product catalog — shoppers will still see it" : undefined}
                      >
                        {p}
                        <button type="button" className="rpf-chip-x" onClick={() => removeProduct(p)} aria-label={`Remove ${p}`}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              {products.some(p => !matchProductName(p, catalog).matched) && !!catalog.names?.length && (
                <span className="rpf-field-note">
                  Highlighted names aren’t in your product catalog. They still show on your
                  website — add them in System Settings → Products to keep the filter tidy.
                </span>
              )}
              {location?.origin === "depletions" && (
                <span className="rpf-field-note">
                  This store came from your depletion data, so its products are rebuilt from
                  what was actually sold here — edits to this list won’t survive the next sync.
                </span>
              )}
            </div>

            <label className="rpf-field rpf-col-3">
              <span>Internal Notes <em>(never shown on the website)</em></span>
              <textarea rows={3} value={form.notes || ""} onChange={e => set("notes", e.target.value)} />
            </label>

            <label className="rpf-check rpf-col-3">
              <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} />
              <span>Active — show this location on the website product finder map</span>
            </label>
          </div>
        </div>

        <div className="rpf-modal-foot">
          <button className="rpf-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rpf-btn rpf-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Location"}
          </button>
        </div>
      </div>
    </div>
  );
}
